/**
 * Central annonspolicy — en enda källa till sanning för NÄR Google-annonser
 * får laddas och visas. Syftet är att aldrig bryta mot AdSense-policyn
 * "Google-served ads on screens without publisher content" och att aldrig
 * ladda annonsskriptet innan användaren gjort ett cookie-val (GDPR/EEA).
 *
 * Regler:
 *  1. Inga annonser utan cookie-beslut (accepterat ELLER nekat → NPA).
 *  2. Inga annonser på verktygs-, konto-, admin-, formulär- och felsidor.
 *  3. Inga annonser på sidor som är noindex (tunt innehåll enligt kvalitetsgrinden).
 *  4. Inga annonser på sidor med för lite redaktionellt innehåll (< MIN_WORDS ord).
 *  5. Inga annonser i sökresultat utan träffar eller på 404/fel.
 */

import { ADS_ENABLED, AUTO_ADS_ON } from "@/lib/ads-config";

export const AD_CLIENT = "ca-pub-7678537574282372";

/** Minsta antal ord i <main> för att en redaktionell sida får visa annonser. */
export const MIN_CONTENT_WORDS = 350;

/** Lägre tröskel för översiktssidor (lager, start) där innehållet är listbaserat. */
export const MIN_LOW_CONTENT_WORDS = 150;

/** Sidor som ALDRIG får annonser (konto, admin, formulär, transaktion, fel). */
const BLOCKED_PREFIXES = [
  "/admin",
  "/konto",
  "/login",
  "/logga-in",
  "/sparade",
  "/unsubscribe",
  "/kontakt",
  "/prisvakt",
  "/husbil-matchning",
  "/forum/nytt",
  "/forum/bevakningar",
  "/forum/sok",
  "/campingtips/ny",
  "/erfarenheter",
  "/api",
  "/email",
  "/lovable",
  "/.well-known",
  "/mcp",
  "/webbkarta",
  "/cookies",
  "/integritetspolicy",
  "/anvandarvillkor",
  // Enskilda fordonsannonser: ren köpupplevelse, inga annonser
  "/fordon/",
  "/salda/",
];

export type AdDensity = "off" | "low" | "high";

/**
 * Fristående redaktionella guider som ligger direkt under roten. De har lika
 * mycket innehåll som bloggen och räknas därför som hög densitet.
 */
const CONTENT_GUIDE_PATHS = [
  "/fukttest-husbil",
  "/stallplatser-husbil",
  "/tvatta-husvagn",
  "/besiktning-husbil",
  "/dieselvarmare-husbil",
  "/solceller-husbil",
  "/husbilsdack",
  "/husbilsforsakring",
  "/b96-korkort",
  "/salja-husbil",
  "/prisstatistik-husbil",
];


/** Innehållstunga sektioner där annonser är relevanta och tillåtna. */
const CONTENT_PREFIXES = [
  "/blogg",
  "/guider",
  "/kunskap",
  "/rapido",
  "/campingtips",
  "/ordlista",
  "/tester",
  "/jamfor",
  "/fakta",
  "/husbilar",
  "/husvagnar",
  "/husbil",
  "/leasa-husbil",
  "/prisindex",
  "/borsen",
  "/marknad",
  "/forum",
];

/**
 * Kommersiella lead- och köpsidor. De rankar bra men tjänar mer på en
 * förfrågan än på en annonsklick — därför max EN diskret annons i sidfoten
 * och aldrig Auto Ads mitt i innehållet.
 */
const LEAD_PREFIXES = [
  "/husbilar-till-salu",
  "/husvagnar-till-salu",
  "/vardera-husbil",
  "/boka-service",
  "/omdomen",
  "/husbil-resa",
  "/verktyg",
  // Lokala landningssidor (husbil-kalix, husbil-lulea, /husbil/<ort>/…)
  "/husbil-kalix",
  "/husbil-lulea",
  "/husbil-boden",
  "/husbil-pitea",
  "/husbil-haparanda",
  "/husbil-norrbotten",
  "/husbil/",
];

function isLeadPath(p: string): boolean {
  return LEAD_PREFIXES.some((l) => p === l.replace(/\/$/, "") || p.startsWith(l));
}

export function isBlockedPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return BLOCKED_PREFIXES.some((b) => p === b || p.startsWith(`${b}/`) || p.startsWith(b));
}

/**
 * Annonsdensitet per route.
 *  - "low"  = start-/översikts-/leadsidor: max 1 annons i sidfoten.
 *  - "high" = redaktionella sidor (blogg, guider, kunskap, campingtips…).
 */
export function adDensityForPath(pathname: string): AdDensity {
  if (isBlockedPath(pathname)) return "off";
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p === "/" || p === "/fordon" || p === "/salda" || p === "/fritid" || p === "/reservdelar") {
    return "low";
  }
  if (isLeadPath(p)) return "low";
  if (CONTENT_GUIDE_PATHS.includes(p)) return "high";
  const isContent = CONTENT_PREFIXES.some((c) => p === c || p.startsWith(`${c}/`));
  if (!isContent) return "off";
  return "high";
}



/** Cookie-beslut (samma nyckel som CookieConsent använder). */
export function consentDecision(): "accepted" | "rejected" | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem("ak_cookie_consent_v1");
    return v === "accepted" || v === "rejected" ? v : null;
  } catch {
    return null;
  }
}

/** True om sidan är markerad noindex (kvalitetsgrinden fällde den). */
export function pageIsNoindex(): boolean {
  if (typeof document === "undefined") return false;
  const metas = Array.from(document.querySelectorAll('meta[name="robots"], meta[name="googlebot"]'));
  return metas.some((m) => (m.getAttribute("content") || "").toLowerCase().includes("noindex"));
}

/** Räknar ord i huvudinnehållet (exklusive nav/footer/annonser). */
export function mainContentWordCount(): number {
  if (typeof document === "undefined") return 0;
  const main = document.querySelector("main");
  if (!main) return 0;
  const clone = main.cloneNode(true) as HTMLElement;
  clone.querySelectorAll("aside, script, style, nav, form, [data-nosnippet]").forEach((el) => el.remove());
  const text = (clone.textContent || "").replace(/\s+/g, " ").trim();
  if (!text) return 0;
  return text.split(" ").filter((w) => w.length > 1).length;
}

/**
 * Slutgiltig kontroll som körs i klienten precis innan annonser laddas/pushas.
 * Alla villkor måste vara uppfyllda.
 */
export function adsAllowedNow(pathname: string): boolean {
  if (typeof window === "undefined") return false;
  if (!ADS_ENABLED) return false;
  if (consentDecision() === null) return false;
  const density = adDensityForPath(pathname);
  if (density === "off") return false;
  if (pageIsNoindex()) return false;
  if (document.querySelector("[data-ads='off']")) return false;
  const min = density === "low" ? MIN_LOW_CONTENT_WORDS : MIN_CONTENT_WORDS;
  if (mainContentWordCount() < min) return false;
  return true;
}

/**
 * Auto Ads får bara köra på redaktionella sidor (hög densitet). Start-,
 * fordons-, sålda- och kontosidor styr vi enbart med manuella slots.
 */
export function autoAdsAllowedNow(pathname: string): boolean {
  if (!AUTO_ADS_ON) return false;
  if (adDensityForPath(pathname) !== "high") return false;
  return adsAllowedNow(pathname);
}

/** True om AdSense-skriptet alls ska laddas på den här sidan. */
export function adScriptAllowedNow(pathname: string): boolean {
  return adsAllowedNow(pathname);
}



/** Personaliserade annonser bara vid uttryckligt samtycke. */
export function personalizedAdsAllowed(): boolean {
  return consentDecision() === "accepted";
}
